// Application root: session lifecycle, tenant switching, sign-out, policy changes, cross-tab sync,
// the desktop (F4) sign-in path, the browser's desktop-authorization page (B4) and the identity
// workflow pages /invite and /recover (B6).
import type { Session } from '@bcio-voice/contracts';
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { ApiClient, ApiRequestError, type DataEnvelope, isApiRequestError } from '../lib/api.ts';
import { describeError, type ErrorView } from '../lib/errorCopy.ts';
import { type AuthState, type SignOutNotice, authReducer, csrfFor, currentSession, msUntilExpiry } from '../state/session.ts';
import { WorkspaceProvider } from '../state/workspace.tsx';
import { SignIn, TenantChooser } from '../features/auth/SignIn.tsx';
import { Shell } from './Shell.tsx';
import { StatePanel } from '../ui/StatePanel.tsx';
import { Dialog } from '../ui/Dialog.tsx';
import { APP_NAME } from '../config/brand.ts';
import type { CallBridge } from '../call/bridge.ts';
import type { BridgeContext, PhoneLifecycle } from '../state/workspace.tsx';
import { type DesktopBridge, DesktopContext } from '../desktop/bridge.ts';
import { DesktopSignIn } from '../desktop/DesktopSignIn.tsx';
import { useRouter } from '../lib/router.tsx';
import { DESKTOP_AUTHORIZE_PATH, parseAuthorizeSearch } from '../desktop/authorize.ts';
import { callLaunchIntents } from '../call/launchIntents.ts';
import { FragmentVault, identityRouteFor } from '../identity/fragment.ts';
import { InvitePage, OpenInBrowserNotice, RecoverPage } from '../identity/IdentityPages.tsx';
import { type SessionActions, SessionActionsContext } from '../state/sessionActions.ts';
import { AUTHORIZE_SIGN_IN_CONTEXT, AUTHORIZE_TENANT_CONTEXT, DesktopAppNotice, DesktopAuthorizePage, InvalidAuthorizeLink, authorizeScreen } from '../desktop/DesktopAuthorizePage.tsx';

const CHANNEL = 'sidetone-session';

/** Waits for the phone to stop, but never longer than `ms`. */
async function bounded(task: Promise<unknown> | undefined, ms: number): Promise<void> {
  if (!task) return;
  await Promise.race([task.then(() => undefined, () => undefined), new Promise<void>(resolve => setTimeout(resolve, ms))]);
}

export function App({ bridgeFactory, desktop = null, fragments: suppliedFragments }: { bridgeFactory?: (context: BridgeContext) => CallBridge; desktop?: DesktopBridge | null; fragments?: FragmentVault }) {
  // main.tsx captures and scrubs /invite and /recover fragments before the first render.
  const [fragments] = useState(() => suppliedFragments ?? new FragmentVault());
  const phoneLifecycle = useRef<PhoneLifecycle | null>(null);
  const registerTeardown = useCallback((phone: PhoneLifecycle | null) => { phoneLifecycle.current = phone; }, []);
  const [auth, dispatch] = useReducer(authReducer, { kind: 'booting' } as AuthState);
  const authRef = useRef(auth);
  authRef.current = auth;
  const liveCall = useRef(false);
  const [reauthOpen, setReauthOpen] = useState(false);
  const [signOutConfirm, setSignOutConfirm] = useState(false);
  const channel = useMemo(() => (typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(CHANNEL)), []);

  const fetchSession = useCallback(async (client: ApiClient): Promise<Session | null> => {
    try {
      return (await client.request<DataEnvelope<Session>>('/auth/session')).data;
    } catch {
      return null;
    }
  }, []);

  /** Ends this sign-in: the desktop main process revokes on desktop, the API does in a browser. */
  const endSession = useCallback(async (client: ApiClient) => {
    callLaunchIntents().clear();
    await bounded(phoneLifecycle.current?.stop(), 4000);
    if (desktop) await bounded(desktop.auth.signOut(), 5000);
    else {
      try {
        await client.request<void>('/auth/logout', { method: 'POST' });
      } catch {
        // Fall through to a fresh anonymous session either way.
      }
    }
  }, [desktop]);

  const policyRef = useRef<(reason: string | null) => void>(() => undefined);
  const expiredOnce = useRef(false);
  const api = useMemo(() => {
    const client: ApiClient = new ApiClient({
      getCsrfToken: () => csrfFor(authRef.current),
      onUnauthenticated: error => {
        if (error.code !== 'unauthenticated' || expiredOnce.current) return;
        const state = authRef.current;
        if (state.kind !== 'ready' && state.kind !== 'expired_in_call') return;
        expiredOnce.current = true;
        void fetchSession(client).then(anonymous => {
          expiredOnce.current = false;
          dispatch({ type: 'expired', anonymous: anonymous && !anonymous.user ? anonymous : null, liveCall: liveCall.current });
        });
      },
      onCsrfRejected: () => {
        void fetchSession(client).then(s => { if (s) dispatch({ type: 'session_loaded', session: s }); });
      },
      onPolicyRejected: error => policyRef.current(error.code),
    });
    return client;
  }, [fetchSession]);

  const boot = useCallback(() => {
    void api.request<DataEnvelope<Session>>('/auth/session').then(
      env => dispatch({ type: 'session_loaded', session: env.data }),
      error => dispatch({ type: 'boot_failed', error: isApiRequestError(error) ? error : null }),
    );
  }, [api]);
  useEffect(boot, [boot]);

  // Other tabs: a sign-in, sign-out or tenant switch elsewhere reloads this tab's session.
  useEffect(() => {
    if (!channel) return;
    channel.onmessage = () => { boot(); };
    return () => channel.close();
  }, [channel, boot]);
  const broadcast = useCallback(() => channel?.postMessage({ type: 'session-changed' }), [channel]);

  // Session expiry timer: confirm with the server when expires_at passes.
  const session = currentSession(auth);
  useEffect(() => {
    if (auth.kind === 'booting' || auth.kind === 'boot_error' || auth.kind === 'choose_tenant') return;
    callLaunchIntents().bindAccount(session?.user?.id && session.tenant?.id ? `${session.tenant.id}:${session.user.id}` : null);
  }, [auth.kind, session?.user?.id, session?.tenant?.id]);
  useEffect(() => {
    if (auth.kind !== 'ready') return;
    const ms = msUntilExpiry(session, Date.now());
    if (ms === null) return;
    const t = setTimeout(() => {
      void fetchSession(api).then(s => {
        if (s?.user && s.tenant) dispatch({ type: 'session_loaded', session: s });
        else dispatch({ type: 'expired', anonymous: s, liveCall: liveCall.current });
      });
    }, Math.min(ms + 1000, 2 ** 31 - 1));
    return () => clearTimeout(t);
  }, [auth.kind, session, api, fetchSession]);

  const onSignedIn = useCallback((s: Session) => {
    setReauthOpen(false);
    dispatch({ type: 'session_loaded', session: s });
    broadcast();
  }, [broadcast]);

  const signOut = useCallback(async () => {
    setSignOutConfirm(false);
    await endSession(api);
    const anonymous = await fetchSession(api);
    if (anonymous && !anonymous.user) dispatch({ type: 'signed_out', anonymous });
    else boot();
    broadcast();
  }, [api, endSession, fetchSession, boot, broadcast]);

  const requestSignOut = useCallback(() => {
    if (liveCall.current) setSignOutConfirm(true);
    else void signOut();
  }, [signOut]);

  /**
   * The role's policy no longer accepts this session (SSE `auth.policy_changed` or a 403
   * `mfa_required`). Sensitive views are already cleared by the workspace. A live call keeps the
   * workspace with a sign-in banner; otherwise the session ends and a fresh sign-in is required.
   */
  const onPolicyChanged = useCallback(async () => {
    const state = authRef.current;
    if (state.kind === 'signed_out' || state.kind === 'booting') return;
    if (liveCall.current && state.kind === 'ready') {
      dispatch({ type: 'expired', anonymous: state.session, liveCall: true, notice: 'policy' });
      return;
    }
    await endSession(api);
    const anonymous = await fetchSession(api);
    dispatch({ type: 'expired', anonymous: anonymous && !anonymous.user ? anonymous : null, liveCall: false, notice: 'policy' });
    broadcast();
  }, [api, endSession, fetchSession, broadcast]);
  policyRef.current = () => { void onPolicyChanged(); };

  /** After a media-access change: re-read the session. True when the same session is still valid. */
  const refreshSession = useCallback(async (): Promise<boolean> => {
    const before = currentSession(authRef.current);
    const s = await fetchSession(api);
    if (s?.user && s.tenant && s.user.id === before?.user?.id && s.tenant.id === before?.tenant?.id) {
      dispatch({ type: 'session_loaded', session: s });
      return true;
    }
    if (s?.user) dispatch({ type: 'session_loaded', session: s });
    else dispatch({ type: 'expired', anonymous: s, liveCall: liveCall.current });
    return false;
  }, [api, fetchSession]);

  const switchTenant = useCallback(async (tenantId: string): Promise<ErrorView | null> => {
    if (liveCall.current) return { title: 'Finish your call before switching workspaces.', detail: '', code: 'call_active', requestId: null, retryable: false };
    // B4: a desktop sign-in belongs to one workspace. Switching means signing out there, then a new
    // browser sign-in that chooses the other workspace; the API refuses an in-place switch.
    if (desktop) return describeError(new ApiRequestError({ status: 403, code: 'desktop_new_authorization_required', message: '' }), "Couldn't switch workspace");
    try {
      callLaunchIntents().clear();
      await bounded(phoneLifecycle.current?.stop(), 4000);
      const env = await api.send<DataEnvelope<Session>>('POST', '/auth/tenant', { tenant_id: tenantId });
      dispatch({ type: 'session_loaded', session: env.data });
      broadcast();
      return null;
    } catch (error) {
      phoneLifecycle.current?.resume();
      return describeError(error, "Couldn't switch workspace");
    }
  }, [api, broadcast, desktop]);

  /** Re-reads the session (the desktop-authorization page uses it after a workspace mismatch). */
  const reloadSession = useCallback(async () => {
    const s = await fetchSession(api);
    if (s) dispatch({ type: 'session_loaded', session: s });
  }, [api, fetchSession]);

  /**
   * B6 workflows that end this sign-in on purpose (password reset and two-step enrollment revoke
   * every session; joining a workspace needs a new one): stop the phone, end the session, clear
   * the workspace and show the normal sign-in. Never mints or keeps a session.
   */
  const requireFreshSignIn = useCallback(async (notice: SignOutNotice) => {
    setReauthOpen(false);
    await endSession(api);
    const anonymous = await fetchSession(api);
    if (anonymous && !anonymous.user) dispatch({ type: 'signed_out', anonymous, notice });
    else dispatch({ type: 'expired', anonymous: null, liveCall: false });
    broadcast();
  }, [api, endSession, fetchSession, broadcast]);

  const sessionActions = useMemo<SessionActions>(() => ({ requireFreshSignIn, reloadSession }), [requireFreshSignIn, reloadSession]);

  const onLiveCallChange = useCallback((live: boolean) => {
    const was = liveCall.current;
    liveCall.current = live;
    if (was && !live && authRef.current.kind === 'expired_in_call') dispatch({ type: 'call_finished' });
  }, []);

  const onStreamExpired = useCallback(() => {
    void fetchSession(api).then(s => {
      if (s?.user && s.tenant && s.tenant.id === currentSession(authRef.current)?.tenant?.id && (s.view_as?.user_id ?? null) === (currentSession(authRef.current)?.view_as?.user_id ?? null)) return;
      if (s?.user) dispatch({ type: 'session_loaded', session: s });
      else dispatch({ type: 'expired', anonymous: s, liveCall: liveCall.current });
    });
  }, [api, fetchSession]);

  // Desktop: the main process owns the credential. If it signs out or loses the credential
  // (revocation, failed refresh), this renderer follows; a call in progress keeps the workspace.
  useEffect(() => {
    if (!desktop) return;
    return desktop.auth.onChange(state => {
      const current = authRef.current;
      if ((state.status === 'signed_out' || state.status === 'unconfigured') && (current.kind === 'ready' || current.kind === 'choose_tenant')) {
        void fetchSession(api).then(s => dispatch({ type: 'expired', anonymous: s && !s.user ? s : null, liveCall: liveCall.current }));
      }
    });
  }, [desktop, api, fetchSession]);

  const signInScreen = (notice: Parameters<typeof SignIn>[0]['notice'], embedded = false) =>
    desktop ? <DesktopSignIn desktop={desktop} api={api} notice={notice} onSignedIn={onSignedIn} embedded={embedded} /> : <SignIn api={api} notice={notice} onSignedIn={onSignedIn} embedded={embedded} />;
  const onChosen = (s: Session) => { dispatch({ type: 'session_loaded', session: s }); broadcast(); };

  // /desktop/authorize keeps its exact URL through sign-in, the code step and workspace choice;
  // it never mounts the workspace (no phone, event stream or workspace data).
  // Going Back onto this route during a call keeps the workspace (and the call) mounted.
  const router = useRouter();
  const navigate = router.navigate;
  useEffect(() => {
    const onLaunch = () => navigate('/call');
    window.addEventListener('sidetone-call-launch', onLaunch);
    if (callLaunchIntents().getSnapshot().pending && window.location.pathname !== '/call') navigate('/call');
    return () => window.removeEventListener('sidetone-call-launch', onLaunch);
  }, [navigate]);
  const returnPathRef = useRef<string | null>(null);

  const startViewAs = useCallback(async (userId: string): Promise<ErrorView | null> => {
    if (liveCall.current) return { title: 'Finish your call before viewing as another person.', detail: '', code: 'call_active', requestId: null, retryable: false };
    try {
      await bounded(phoneLifecycle.current?.stop(), 4000);
      const env = await api.send<DataEnvelope<Session>>('POST', '/admin/view-as', { user_id: userId });
      returnPathRef.current = router.path;
      dispatch({ type: 'session_loaded', session: env.data });
      if (router.path.startsWith('/admin') || router.path.startsWith('/analytics') || router.path.startsWith('/routing') || router.path.startsWith('/flow') || router.path.startsWith('/blocklist')) {
        router.navigate('/inbox/calls');
      }
      broadcast();
      return null;
    } catch (error) {
      phoneLifecycle.current?.resume();
      return describeError(error, "Couldn't start viewing as this person");
    }
  }, [api, broadcast, router]);

  const stopViewAs = useCallback(async (): Promise<ErrorView | null> => {
    try {
      const env = await api.send<DataEnvelope<Session>>('DELETE', '/admin/view-as');
      dispatch({ type: 'session_loaded', session: env.data });
      const back = returnPathRef.current;
      returnPathRef.current = null;
      if (back) router.navigate(back);
      broadcast();
      return null;
    } catch (error) {
      return describeError(error, "Couldn't exit viewing mode");
    }
  }, [api, broadcast, router]);

  // View-as expiry timer: reload session when view_as.expires_at passes.
  useEffect(() => {
    if (auth.kind !== 'ready' || !session?.view_as?.expires_at) return;
    const ms = Date.parse(session.view_as.expires_at) - Date.now();
    const delay = Math.max(0, Math.min(ms + 500, 2 ** 31 - 1));
    const t = setTimeout(() => {
      void fetchSession(api).then(s => {
        if (s?.user) dispatch({ type: 'session_loaded', session: s });
      });
    }, delay);
    return () => clearTimeout(t);
  }, [auth.kind, session?.view_as?.expires_at, api, fetchSession]);
  const authorizing = router.path === DESKTOP_AUTHORIZE_PATH && !(liveCall.current && (auth.kind === 'ready' || auth.kind === 'expired_in_call'));
  const authorizeRequest = authorizing ? parseAuthorizeSearch(router.query) : null;
  const identityRoute = liveCall.current && (auth.kind === 'ready' || auth.kind === 'expired_in_call') ? null : identityRouteFor(router.path);
  const identityContent = (() => {
    if (!identityRoute || auth.kind === 'booting' || auth.kind === 'boot_error') return null;
    if (desktop) return <OpenInBrowserNotice kind={identityRoute} />;
    const session = currentSession(auth);
    if (!session) return null;
    const props = { api, session, fragments, onSignOut: () => void signOut(), onRequireFreshSignIn: requireFreshSignIn, onReloadSession: reloadSession };
    return identityRoute === 'invite' ? <InvitePage {...props} /> : <RecoverPage {...props} />;
  })();
  const authorizeContent = (() => {
    if (!authorizing) return null;
    switch (authorizeScreen(auth.kind, desktop !== null, authorizeRequest)) {
      case 'desktop_app':
        return <DesktopAppNotice />;
      case 'invalid_link':
        return <InvalidAuthorizeLink />;
      case 'sign_in':
        return <SignIn api={api} notice={auth.kind === 'signed_out' ? auth.notice : 'expired'} onSignedIn={onSignedIn} context={AUTHORIZE_SIGN_IN_CONTEXT} />;
      case 'choose_tenant':
        return auth.kind === 'choose_tenant' ? <TenantChooser api={api} session={auth.session} onChosen={onChosen} onSignOut={() => void signOut()} context={AUTHORIZE_TENANT_CONTEXT} /> : <InvalidAuthorizeLink />;
      case 'confirm':
        return auth.kind === 'ready' && authorizeRequest ? (
          <DesktopAuthorizePage
            key={authorizeRequest}
            api={api}
            request={authorizeRequest}
            session={auth.session}
            onSwitchTenant={switchTenant}
            onSignOut={() => void signOut()}
            onReauthenticate={() => void onPolicyChanged()}
            onReloadSession={reloadSession}
          />
        ) : <InvalidAuthorizeLink />;
      default:
        return null; // booting and boot errors render as everywhere else
    }
  })();

  const content = identityContent ?? authorizeContent ?? (() => {
    switch (auth.kind) {
      case 'booting':
        return <main id="main" className="auth-page"><StatePanel variant="loading" title={`Opening ${APP_NAME}…`} /></main>;
      case 'boot_error':
        return (
          <main id="main" className="auth-page">
            <StatePanel variant={auth.error?.code === 'network_error' ? 'offline' : 'error'} title={`Couldn't start ${APP_NAME}`} body={auth.error ? describeError(auth.error).title : 'The server did not return a session.'} action={<button type="button" className="btn" onClick={boot}>Try again</button>} details={{ code: auth.error?.code ?? null, requestId: auth.error?.requestId ?? null }} />
          </main>
        );
      case 'signed_out':
        return signInScreen(auth.notice);
      case 'choose_tenant':
        return <TenantChooser api={api} session={auth.session} onChosen={onChosen} onSignOut={() => void signOut()} />;
      default: {
        const s = auth.session;
        const expired = auth.kind === 'expired_in_call';
        const policy = expired && auth.notice === 'policy';
        return (
          <>
            <WorkspaceProvider
              key={`${s.user?.id}:${s.tenant?.id}:${s.view_as?.user_id ?? ''}`}
              startViewAs={startViewAs}
              stopViewAs={stopViewAs}
              api={api}
              session={s}
              onSessionExpired={onStreamExpired}
              onLiveCallChange={onLiveCallChange}
              registerTeardown={registerTeardown}
              refreshSession={refreshSession}
              onPolicyChanged={() => { void onPolicyChanged(); }}
              sensitiveBlocked={expired}
              {...(bridgeFactory ? { bridgeFactory } : {})}
            >
              <Shell
                expiredBanner={expired ? (
                  <div className="session-banner" role="alert">
                    {policy ? 'Your access changed. Your call continues. Sign in again, including your verification code, to keep using call controls.' : 'Your session expired. Your call continues. Sign in again after this call, or now to keep using call controls.'}
                    <button type="button" className="btn btn-small" onClick={() => setReauthOpen(true)}>Sign in again</button>
                  </div>
                ) : null}
                onSwitchTenant={switchTenant}
                onSignOut={requestSignOut}
              />
            </WorkspaceProvider>
            <Dialog open={expired && reauthOpen} title="Sign in again" onClose={() => setReauthOpen(false)}>
              {expired ? signInScreen(policy ? 'policy' : 'expired', true) : null}
            </Dialog>
            <Dialog open={signOutConfirm} title="End the call and sign out?" onClose={() => setSignOutConfirm(false)}
              footer={<><button type="button" className="btn" onClick={() => setSignOutConfirm(false)}>Stay signed in</button><button type="button" className="btn btn-end" onClick={() => void signOut()}>Sign out</button></>}>
              <p>Signing out revokes this {desktop ? 'computer' : 'browser'}’s phone. Your current call may end.</p>
            </Dialog>
          </>
        );
      }
    }
  })();

  return (
    <DesktopContext.Provider value={desktop}>
      <SessionActionsContext.Provider value={sessionActions}>{content}</SessionActionsContext.Provider>
    </DesktopContext.Provider>
  );
}
